import { useState, useEffect } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { GpsTrip } from '@/hooks/use-gps-trips';
import { Play, Eye } from 'lucide-react';

interface TripsListCompactProps {
  trips: GpsTrip[];
  onTripSelect: (tripId: string) => void;
  selectedTripId: string | null;
  tripPositions: Array<{
    id: string;
    fix_time: string;
    lat: number;
    lng: number;
    speed_kmh: number | null;
    course_deg: number | null;
    ignition: boolean | null;
  }>;
  onStartReplay?: (positions: TripsListCompactProps['tripPositions']) => void;
  onViewTrajectory?: (tripId: string) => void;
}

export function TripsListCompact({
  trips,
  onTripSelect,
  selectedTripId,
  tripPositions,
  onStartReplay,
  onViewTrajectory,
}: TripsListCompactProps) {
  const [shouldStartReplay, setShouldStartReplay] = useState(false);

  // Iniciar replay quando posições carregarem
  useEffect(() => {
    if (shouldStartReplay && tripPositions.length > 0 && onStartReplay) {
      onStartReplay(tripPositions);
      setShouldStartReplay(false);
    }
  }, [tripPositions, shouldStartReplay, onStartReplay]);

  // Estado de lista vazia
  if (trips.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Nenhuma viagem encontrada no período
      </div>
    );
  }

  // Lista de viagens
  return (
    <ScrollArea className="h-full">
      <div className="px-4 py-2 space-y-2">
        {trips.map((trip) => (
          <button
            key={trip.id}
            onClick={() => onTripSelect(trip.id)}
            className={`
              w-full p-3 rounded-lg border transition-all 
              hover:shadow-md text-left
              ${
                selectedTripId === trip.id
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-background hover:border-primary/50'
              }
            `}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">
                  {new Date(trip.start_time).toLocaleDateString('pt-BR')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(trip.start_time).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}{' '}
                  →{' '}
                  {new Date(trip.end_time).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
              <Badge variant="outline" className="text-xs">
                {trip.distance_km.toFixed(1)} km
              </Badge>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 text-center mb-2">
              <div>
                <p className="text-[10px] text-muted-foreground">Duração</p>
                <p className="text-xs font-semibold">
                  {Math.floor(trip.duration_minutes / 60)}h {trip.duration_minutes % 60}m
                </p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Parado</p>
                <p className="text-xs font-semibold text-orange-600">
                  {Math.floor(trip.idle_time_minutes / 60)}h {trip.idle_time_minutes % 60}m
                </p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Vel. Média</p>
                <p className="text-xs font-semibold text-blue-600">
                  {trip.duration_minutes > 0
                    ? Math.round(trip.distance_km / (trip.duration_minutes / 60))
                    : 0}{' '}
                  km/h
                </p>
              </div>
            </div>

            {/* Botões de ação */}
            <div className="pt-2 border-t flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 h-8"
                onClick={(e) => {
                  e.stopPropagation();
                  onTripSelect(trip.id);
                  if (onViewTrajectory) {
                    onViewTrajectory(trip.id);
                  }
                }}
              >
                <Eye className="h-3 w-3 mr-1" />
                Ver Trajeto
              </Button>
              
              <Button
                size="sm"
                variant="default"
                className="flex-1 h-8"
                onClick={(e) => {
                  e.stopPropagation();
                  onTripSelect(trip.id);
                  setShouldStartReplay(true);
                }}
              >
                <Play className="h-3 w-3 mr-1" />
                Replay
              </Button>
            </div>
          </button>
        ))}
      </div>
    </ScrollArea>
  );
}
